/* Dependency-free living Banner. Posters remain available when video is paused or unsupported. */
(() => {
  const preference = matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new Map();
  function setup(root) {
    if (instances.has(root)) return;
    const scenes = [...root.querySelectorAll('[data-banner-scene]')];
    const controls = root.querySelector('[data-banner-controls]');
    if (scenes.length !== 2 || !controls) return;
    const videos = scenes.map(scene => scene.querySelector('[data-living-video]'));
    if (videos.some(video => !video)) return;
    const dots = [...controls.querySelectorAll('[data-scene-select]')];
    const toggle = controls.querySelector('[data-scene-toggle]');
    const caption = controls.querySelector('[data-scene-caption]');
    const labels = dots.map(dot => dot.getAttribute('aria-label').replace(/^Show scene \d: /, ''));
    const fade = Math.max(150, Math.min(500, Number(root.dataset.fade) || 250));
    const dataSaving = Boolean(navigator.connection?.saveData);
    const abort = new AbortController();
    const listen = (target,event,handler) => target.addEventListener(event,handler,{signal:abort.signal});
    let current = 0, incoming = null, transition = null, switching = false, destroyed = false;
    let visible = false;
    let paused = root.dataset.autoplay !== 'true' || preference.matches || dataSaving || Boolean(window.Shopify?.designMode);
    let failed = false, serial = 0, cycles = 0;
    const prepared = [null,null], posters = [null,null], requests = [null,null];
    const running = () => !paused && !failed && visible && !document.hidden && !destroyed;

    function stopPump(index) {
      const id = requests[index];
      if (id !== null) {
        if (videos[index].cancelVideoFrameCallback) videos[index].cancelVideoFrameCallback(id);
        else cancelAnimationFrame(id);
      }
      requests[index] = null;
    }
    function paint(index) {
      const video = videos[index];
      if (destroyed || video.readyState < 2 || !video.videoWidth) return;
      scenes[index].classList.add('is-video-ready');
      video.dataset.renderedTime = video.currentTime.toFixed(3);
      video.dataset.framesSeen = String(Number(video.dataset.framesSeen || 0) + 1);
    }
    function clearTransition() {
      transition?.cancel(); transition = null;
      if (incoming !== null) {
        scenes[incoming].classList.remove('is-entering');
        incoming = null;
      }
    }
    function unavailable(error) {
      if (destroyed || failed) return;
      failed = true; paused = true; serial++;
      scenes.forEach(scene => scene.classList.remove('is-video-ready'));
      clearTransition();
      switching = false;
      scenes[current].classList.add('is-active');
      root.dataset.motionError = error?.message || 'Video unavailable';
      sync();
    }
    function preparePoster(index) {
      if (posters[index]) return posters[index];
      const image = scenes[index].querySelector('img');
      posters[index] = new Promise((resolve,reject) => {
        let finished = false;
        const timer = setTimeout(() => finish(new Error('Poster load timeout')),15000);
        function finish(error) {
          if (finished) return;
          finished = true; clearTimeout(timer);
          image.removeEventListener('load',loaded); image.removeEventListener('error',failedPoster);
          abort.signal.removeEventListener('abort',cancelled);
          error ? reject(error) : resolve();
        }
        function loaded() {image.decode ? image.decode().then(() => finish(),finish) : finish()}
        function failedPoster() {finish(new Error('Poster unavailable'))}
        function cancelled() {finish(new Error('Section removed'))}
        image.addEventListener('load',loaded); image.addEventListener('error',failedPoster);
        abort.signal.addEventListener('abort',cancelled,{once:true});
        scenes[index].querySelectorAll('[data-scene-srcset]').forEach(el => {el.srcset = el.dataset.sceneSrcset});
        if (image.dataset.sceneSrc) image.src = image.dataset.sceneSrc;
        if (image.complete && image.naturalWidth) loaded();
      });
      return posters[index];
    }
    function prepare(index) {
      if (prepared[index]) return prepared[index];
      prepared[index] = preparePoster(index).then(() => new Promise((resolve,reject) => {
        if (destroyed) {reject(new Error('Section removed'));return}
        const video = videos[index];
        let finished = false;
        const timer = setTimeout(() => finish(new Error('Video load timeout')),18000);
        function finish(error) {
          if (finished) return;
          finished = true; clearTimeout(timer);
          video.removeEventListener('loadeddata',loaded); video.removeEventListener('error',badVideo);
          abort.signal.removeEventListener('abort',cancelled);
          error ? reject(error) : resolve();
        }
        function loaded() {try {paint(index);finish()} catch(error) {finish(error)}}
        function badVideo() {finish(new Error('Video unavailable'))}
        function cancelled() {finish(new Error('Section removed'))}
        video.addEventListener('loadeddata',loaded); video.addEventListener('error',badVideo);
        abort.signal.addEventListener('abort',cancelled,{once:true});
        video.muted = true; video.defaultMuted = true; video.playsInline = true;
        if (video.dataset.src && !video.getAttribute('src')) video.src = video.dataset.src;
        video.load();
        if (video.readyState >= 2) loaded();
      }));
      return prepared[index];
    }
    function pump(index) {
      requests[index] = null;
      try {paint(index)} catch(error) {unavailable(error);return}
      if (!running() || videos[index].paused) return;
      if (index === current && !switching && videos[index].ended) change(1-current,false);
      if (!running() || videos[index].ended) return;
      const callback = () => pump(index);
      requests[index] = videos[index].requestVideoFrameCallback ? videos[index].requestVideoFrameCallback(callback) : requestAnimationFrame(callback);
    }
    function play(index) {
      if (!running() || videos[index].ended) return;
      videos[index].play().catch(error => {
        if (destroyed || error.name === 'AbortError') return;
        paused = true; root.dataset.motionError = 'Autoplay paused; press Play to start'; sync();
      });
    }
    function sync() {
      const active = running();
      root.dataset.motionState = failed ? 'fallback' : paused ? 'paused' : active ? 'playing' : 'suspended';
      root.classList.toggle('is-running',active);
      toggle.disabled = failed;
      toggle.setAttribute('aria-label',failed ? 'Banner motion unavailable' : paused ? 'Play banner motion' : 'Pause banner motion');
      toggle.querySelector('[data-scene-toggle-label]').textContent = failed ? 'Still' : paused ? 'Play' : 'Pause';
      toggle.querySelector('[data-scene-play]').toggleAttribute('hidden',!paused);
      toggle.querySelector('[data-scene-pause]').toggleAttribute('hidden',paused);
      if (!active) {
        transition?.pause();
        videos.forEach((video,index) => {video.pause();stopPump(index)});
        return;
      }
      if (transition) {transition.play();return}
      const selected = current;
      prepare(selected).then(() => {
        if (!running() || current !== selected || switching) return;
        if (videos[selected].ended) {change(1-selected,false);return}
        play(selected);
        if (!dataSaving) prepare(1-selected).catch(unavailable);
      }).catch(unavailable);
    }
    function setSelection(index) {
      dots.forEach((dot,number) => {if(index === number) dot.setAttribute('aria-current','true'); else dot.removeAttribute('aria-current')});
      caption.textContent = labels[index];
      scenes.forEach((scene,number) => {if(index === number) scene.removeAttribute('aria-hidden'); else scene.setAttribute('aria-hidden','true')});
      root.dataset.activeScene = String(index+1);
    }
    function resetOffscreen(index) {
      videos[index].pause(); stopPump(index);
      if (videos[index].readyState >= 2) videos[index].currentTime = 0;
    }
    async function change(next,manual) {
      if (destroyed) return;
      if (manual) {
        paused = true; serial++;
        clearTransition();
        switching = false; root.dataset.transition = 'none'; sync();
      }
      if (next === current || switching) return;
      const token = ++serial;
      switching = true;
      try {
        if (manual) await preparePoster(next); else await prepare(next);
        if (destroyed || token !== serial) return;
        if (!manual && !running()) {switching = false;return}
        const previous = current;
        if (manual) {
          scenes[previous].classList.remove('is-active'); scenes[next].classList.add('is-active');
          current = next; switching = false; setSelection(next); resetOffscreen(previous); return;
        }
        incoming = next;
        scenes[next].classList.add('is-entering');
        root.dataset.transition = `${previous+1}-${next+1}`;
        // Crossfade boundary frames; do not overlap two moving performances.
        videos.forEach((video,index) => {video.pause();stopPump(index)});
        transition = scenes[next].animate(
          [{opacity:0},{opacity:1}],
          {duration:fade,easing:'cubic-bezier(.4,0,.2,1)',fill:'forwards'}
        );
        const animation = transition;
        animation.finished.then(() => {
          if (destroyed || token !== serial) return;
          scenes[next].classList.add('is-active'); scenes[next].classList.remove('is-entering');
          scenes[previous].classList.remove('is-active');
          current = next; incoming = null; switching = false; transition = null;
          setSelection(next); root.dataset.transition = 'none'; root.dataset.sceneChanges = String(++cycles);
          animation.cancel(); resetOffscreen(previous); sync();
        }).catch(() => {});
      } catch(error) {unavailable(error)}
    }
    videos.forEach((video,index) => {
      listen(video,'play',() => {stopPump(index);pump(index)});
      listen(video,'seeked',() => {try {paint(index)} catch(error) {unavailable(error)}});
      listen(video,'error',() => {if(video.hasAttribute('src') || video.querySelector('source')) unavailable(new Error('Video unavailable'))});
      listen(video,'ended',() => {if(index === current && !switching && running()) change(1-current,false)});
    });
    listen(toggle,'click',() => {paused = !paused;sync()});
    dots.forEach((dot,index) => listen(dot,'click',() => change(index,true)));
    listen(document,'visibilitychange',sync);
    listen(preference,'change',() => {if(preference.matches) paused = true;sync()});
    const observer = new IntersectionObserver(entries => {visible = entries[0].isIntersecting;sync()},{threshold:.12});
    observer.observe(root);
    root.dataset.activeScene = '1'; root.dataset.sceneChanges = '0'; root.dataset.transition = 'none';
    controls.hidden = false;
    sync();
    instances.set(root,() => {
      destroyed = true; serial++; clearTransition(); observer.disconnect(); abort.abort();
      videos.forEach((video,index) => {stopPump(index);video.pause();if(video.dataset.src) video.removeAttribute('src')});
      instances.delete(root);
    });
  }
  const init = scope => scope.querySelectorAll('[data-living-banner]').forEach(setup);
  init(document);
  document.addEventListener('shopify:section:load',event => init(event.target));
  document.addEventListener('shopify:section:unload',event => {instances.forEach((destroy,root) => {if(event.target.contains(root)) destroy()})});
})();
